import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Title } from 'react-admin';
import { API_BASE_URL_ADMIN } from 'shared/config';
import { API_URL } from 'shared/constants/api-url';
import { httpClient } from 'shared/services/http-client';

type GroupCallLink = {
  id: string;
  companyId: string;
  url: string;
  startsAt: string;
  name: string | null;
  description: string | null;
};

type Company = {
  id: string;
  name: string;
};

export type GroupCallForm = {
  id?: string;
  companyId: string;
  url: string;
  startsAt: string;
  name?: string;
  description?: string;
};

const EMPTY_FORM: GroupCallForm = {
  companyId: '',
  url: '',
  startsAt: '',
  name: '',
  description: '',
};

export const toLocalDateTime = (value: string) => {
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

export const validateGroupCall = (
  form: GroupCallForm,
  now = Date.now(),
): string | null => {
  if (!form.companyId) return 'Выберите компанию';
  if (!form.url.trim()) return 'Введите ссылку на созвон';
  if (!/^https:\/\//i.test(form.url.trim())) {
    return 'Ссылка должна начинаться с https://';
  }

  try {
    new URL(form.url);
  } catch {
    return 'Введите корректную HTTPS-ссылку';
  }

  if (!form.startsAt) return 'Укажите дату и время начала';
  const startsAt = new Date(form.startsAt).getTime();
  if (Number.isNaN(startsAt)) return 'Укажите корректную дату и время';
  if (startsAt + 60 * 60 * 1000 <= now) {
    return 'Нельзя сохранить созвон, завершившийся более часа назад';
  }
  return null;
};

const errorMessage = (error: any, fallback: string) =>
  error?.body?.message || error?.message || fallback;

const GroupCallLinks = () => {
  const [items, setItems] = useState<GroupCallLink[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [form, setForm] = useState<GroupCallForm>(EMPTY_FORM);

  const companyNames = useMemo(
    () => new Map(companies.map((company) => [company.id, company.name])),
    [companies],
  );

  const loadList = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await httpClient(
        `${API_BASE_URL_ADMIN}${API_URL.groupCallLinks}`,
      );
      setItems((response.json as GroupCallLink[]) || []);
    } catch (e) {
      setError(errorMessage(e, 'Ошибка загрузки групповых созвонов'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const response = await httpClient(
        `${API_BASE_URL_ADMIN}${API_URL.companies}?limit=1000&page=1`,
      );
      const json = response.json as { data?: Company[] };
      setCompanies(json.data || []);
    } catch (e) {
      setFormError(errorMessage(e, 'Ошибка загрузки компаний'));
    }
  }, []);

  useEffect(() => {
    loadList();
    loadCompanies();
  }, [loadCompanies, loadList]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setIsDialogOpen(true);
    loadCompanies();
  };

  const openEdit = (item: GroupCallLink) => {
    setForm({
      id: item.id,
      companyId: item.companyId,
      url: item.url,
      startsAt: toLocalDateTime(item.startsAt),
      name: item.name || '',
      description: item.description || '',
    });
    setFormError(null);
    setIsDialogOpen(true);
    loadCompanies();
  };

  const closeDialog = () => {
    if (!isSaving) setIsDialogOpen(false);
  };

  const save = async () => {
    const validationError = validateGroupCall(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setIsSaving(true);
    setFormError(null);
    try {
      await httpClient(`${API_BASE_URL_ADMIN}${API_URL.groupCallLinks}`, {
        method: 'POST',
        headers: new Headers({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          ...(form.id && { id: form.id }),
          companyId: form.companyId,
          url: form.url.trim(),
          startsAt: new Date(form.startsAt).toISOString(),
          name: form.name?.trim() || null,
          description: form.description?.trim() || null,
        }),
      });
      setIsDialogOpen(false);
      await loadList();
    } catch (e) {
      setFormError(errorMessage(e, 'Ошибка сохранения ссылки'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Title title="Групповые созвоны" />
      <Card>
        <CardHeader
          title="Групповые созвоны"
          action={
            <Button variant="contained" onClick={openCreate}>
              Добавить ссылку
            </Button>
          }
        />
        {isLoading && <LinearProgress />}
        <CardContent>
          {error && (
            <Stack spacing={2}>
              <Alert severity="error">{error}</Alert>
              <Button onClick={loadList}>Повторить</Button>
            </Stack>
          )}

          {!isLoading && !error && items.length === 0 && (
            <Typography color="text.secondary">
              Активных ссылок пока нет
            </Typography>
          )}

          {!error && items.length > 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Компания</TableCell>
                  <TableCell>Название</TableCell>
                  <TableCell>Описание</TableCell>
                  <TableCell>Дата и время начала</TableCell>
                  <TableCell>Ссылка</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {companyNames.get(item.companyId) || item.companyId}
                    </TableCell>
                    <TableCell>{item.name || '—'}</TableCell>
                    <TableCell>{item.description || '—'}</TableCell>
                    <TableCell>
                      {new Date(item.startsAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Link href={item.url} target="_blank" rel="noreferrer">
                        {item.url}
                      </Link>
                    </TableCell>
                    <TableCell align="right">
                      <Button onClick={() => openEdit(item)}>
                        Редактировать
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {form.id ? 'Редактировать ссылку' : 'Добавить ссылку'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <Autocomplete
              options={companies}
              value={
                companies.find((company) => company.id === form.companyId) ||
                null
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(company) => company.name}
              onChange={(_, company) =>
                setForm((current) => ({
                  ...current,
                  companyId: company?.id || '',
                }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Компания" required />
              )}
            />
            <TextField
              label="Название"
              value={form.name || ''}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
            />
            <TextField
              label="Описание"
              multiline
              minRows={3}
              value={form.description || ''}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
            />
            <TextField
              label="Ссылка на созвон"
              type="url"
              required
              value={form.url}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  url: event.target.value,
                }))
              }
            />
            <TextField
              label="Дата и время начала"
              type="datetime-local"
              required
              value={form.startsAt}
              InputLabelProps={{ shrink: true }}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  startsAt: event.target.value,
                }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isSaving}>
            Отмена
          </Button>
          <Button variant="contained" onClick={save} disabled={isSaving}>
            {isSaving && <CircularProgress size={18} sx={{ mr: 1 }} />}
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupCallLinks;
